import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import "./VillageGallery.css";
import axiosInstance from '../services/axios';

// Import placeholder images - you can replace these with actual village images
import villageBuilding from '../assets/images/villageScene/villageBuilding.png';
import villageTemple from '../assets/images/villageScene/villageTemple.jpeg';
import villageTourist from '../assets/images/villageScene/villageTourist.png';
import villageTank from '../assets/images/villageScene/villageTank.png';
import villageSchool from '../assets/images/villageScene/villageSchool.png';
import villageRoad from '../assets/images/villageScene/villageRoad.png';
import villageWomen from '../assets/images/villageScene/villageWomen.png';

// Map section names to images and routes
const sectionImageMap = {
  temple: { image: villageTemple, route: '/our-village/temple' },
  tourist: { image: villageTourist, route: '/our-village/tourist' },
  water: { image: villageTank, route: '/our-village/water' },
  education: { image: villageSchool, route: '/our-village/education' },
  development: { image: villageRoad, route: '/our-village/development' },
  smallbusiness: { image: villageWomen, route: '/our-village/smallbusiness' },
  administration: { image: villageBuilding, route: '/our-village/administration' }
};

// Section tag to Marathi display name mapping
const sectionMapMr = {
  administration: "प्रशासकीय सेवा",
  development: "विकास कार्य",
  education: "शिक्षण प्रणाली",
  smallbusiness: "लघु उद्योग",
  temple: "गावातील मंदिरे",
  tourist: "पर्यटन स्थळे",
  water: "पाण्याची सुविधा"
};

// Section tag to English display name mapping
const sectionMapEn = {
  administration: "Administrative Services",
  development: "Development Work",
  education: "Education System",
  smallbusiness: "Small Industries",
  temple: "Village Temples",
  tourist: "Tourist Places",
  water: "Water Facilities"
};

// Sections to exclude from the gallery
const excludedSections = ['history', 'village'];

export default function VillageGallery() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch village sections from API
  useEffect(() => {
    let mounted = true;

    const fetchSections = async () => {
      setLoading(true);
      setError(null);

      try {
        const language = i18n.language === 'mr' ? 'Marathi' : 'English';
        const response = await axiosInstance.get('village-sections/', {
          params: { language }
        });

        if (!mounted) return;

        const data = response.data || [];
        if (Array.isArray(data) && data.length > 0) {
          // Filter out excluded sections and map to gallery format
          const mappedSections = data
            .filter(section => !excludedSections.includes(section.name))
            .filter(section => sectionImageMap[section.name]) // Only include sections with mapped routes
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(section => {
              const sectionMap = i18n.language === 'mr' ? sectionMapMr : sectionMapEn;
              return {
                id: section.id,
                title: sectionMap[section.name] || section.name || '',
                description: section.description || '',
                image: section.image || sectionImageMap[section.name]?.image,
                routeLink: `/our-village/${section.name}`
              };
            });

          setSections(mappedSections);
        } else {
          setSections([]);
        }
      } catch (err) {
        console.error('Failed to fetch village sections', err);
        if (mounted) setError(err.message || 'Failed to load sections');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSections();

    return () => { mounted = false; };
  }, [i18n.language]);

  // Handle card click to navigate to child route
  const handleCardClick = (item) => {
    navigate(item.routeLink, {
      state: {
        cardData: item,
        category: 'village-gallery'
      }
    });
  };

  // Fallback static data
  const villageGalleryDataEn = [
    {
      id: 1,
      title: 'Village Temples',
      description: 'Major temples, their specialties and information about their importance.',
      image: villageTemple,
      routeLink: '/our-village/temple'
    },
    {
      id: 2,
      title: 'Tourist Places',
      description: 'Our village has many attractive tourist places that are worth seeing.',
      image: villageTourist,
      routeLink: '/our-village/tourist'
    },
    {
      id: 4,
      title: 'Water Facilities',
      description: 'We are making improvements to ensure access to water facilities for all residents.',
      image: villageTank,
      routeLink: '/our-village/water'
    },
    {
      id: 5,
      title: 'Education System',
      description: 'The education system of our village is developing according to modern standards.',
      image: villageSchool,
      routeLink: '/our-village/education'
    },
    {
      id: 6,
      title: 'Development Work',
      description: 'We are actively participating in various development projects in our village.',
      image: villageRoad,
      routeLink: '/our-village/development'
    },
    {
      id: 7,
      title: 'Small Industries',
      description: 'A prosperous small industry sector is emerging in our village.',
      image: villageWomen,
      routeLink: '/our-village/smallbusiness'
    },
    {
      id: 8,
      title: 'Administrative Services',
      description: 'Efforts are underway to improve the administrative services in our village.',
      image: villageBuilding,
      routeLink: '/our-village/administration'
    }
  ];

  const villageGalleryDataMr = [
    {
      id: 1,
      title: 'गावातील मंदिरे',
      description: 'प्रमुख मंदिरे, त्यांची वैशिष्ट्ये आणि त्यांच्या महत्त्वाविषयी माहिती.',
      image: villageTemple,
      routeLink: '/our-village/temple'
    },
    {
      id: 2,
      title: 'पर्यटन स्थळे',
      description: 'आमच्या गावात अनेक आकर्षक पर्यटन स्थळे आहेत जी पाहण्यासारखी आहेत.',
      image: villageTourist,
      routeLink: '/our-village/tourist'
    },
    {
      id: 4,
      title: 'पाण्याची सुविधा',
      description: 'सर्व रहिवाशांसाठी पाण्याच्या सुविधांचा प्रवेश सुनिश्चित करण्यासाठी आम्ही सुधारणा करत आहोत.',
      image: villageTank,
      routeLink: '/our-village/water'
    },
    {
      id: 5,
      title: 'शिक्षण प्रणाली',
      description: 'आमच्या गावाची शिक्षण प्रणाली आधुनिक मानकांनुसार विकसित होत आहे.',
      image: villageSchool,
      routeLink: '/our-village/education'
    },
    {
      id: 6,
      title: 'विकास कार्य',
      description: 'आम्ही आमच्या गावात विविध विकास प्रकल्पांमध्ये सहभागी आहोत.',
      image: villageRoad,
      routeLink: '/our-village/development'
    },
    {
      id: 7,
      title: 'लघु उद्योग',
      description: 'आमच्या गावात एक समृद्ध लघु उद्योग क्षेत्र उभे राहात आहे.',
      image: villageWomen,
      routeLink: '/our-village/smallbusiness'
    },
    {
      id: 8,
      title: 'प्रशासकीय सेवा',
      description: 'आमच्या गावातील प्रशासकीय सेवा सुधारण्यासाठी प्रयत्न सुरू आहेत.',
      image: villageBuilding,
      routeLink: '/our-village/administration'
    }
  ];

  // Use API data if available, otherwise fallback to static data
  const staticData = i18n.language === 'mr' ? villageGalleryDataMr : villageGalleryDataEn;
  const villageGalleryData = sections.length > 0 ? sections : staticData;

  if (loading) {
    return (
      <div className="village-gallery" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="village-gallery">
      <div className="gallery-grid">
        {villageGalleryData.map((item) => (
          <div
            key={item.id}
            className="gallery-card"
            onClick={() => handleCardClick(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick(item);
              }
            }}
          >
            <div className="gallery-image">
              <img loading="lazy" src={item.image} alt={item.title} />
            </div>
            <div className="gallery-content">
              <h3 className="gallery-title">{item.title}</h3>
              <p className="gallery-description">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

